<template>
  <!-- Localhost Environment: Full Dev UI -->
  <template v-if="isLocalEnvironment">
    <!-- Gradient Background -->
    <div class="app-background"></div>

    <!-- Main Container -->
    <div class="app-container">
      <!-- Hero Section -->
      <header class="hero">
        <div class="hero-content">
          <div class="logo-container">
            <img class="logo" alt="Feegow logo" src="assets/img/logo-feegow.svg" />
            <span class="logo-badge">DEV</span>
          </div>
          <h1 class="hero-title">AI Assistant</h1>
          <p class="hero-subtitle">Development Environment</p>
          <div class="hero-hint">
            <kbd>Ctrl</kbd>
            <span>+</span>
            <kbd>Shift</kbd>
            <span>+</span>
            <kbd>D</kbd>
            <span class="hint-text">to open Dev Panel</span>
          </div>
        </div>
      </header>

      <!-- PMS Simulation Card -->
      <main class="main-content">
        <div class="simulation-card">
          <div class="card-header">
            <div class="card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="24" height="24">
                <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                <line x1="3" x2="21" y1="9" y2="9" />
                <path d="m9 16 2 2 4-4" />
              </svg>
            </div>
            <div class="card-title-group">
              <h2 class="card-title">Feegow PMS Simulation</h2>
              <p class="card-subtitle">Simulated sidebar environment for testing</p>
            </div>
          </div>

          <!-- Feegow PMS DOM structure simulation -->
          <ul id="lMenu" class="sidebar-simulation">
            <li class="row sidebar-stat tray-bin">
              <div id="divContador" class="sidebar-content">
                <button
                  v-if="!attendanceStarted"
                  type="button"
                  class="btn-acao-atendimento btn-success pms-btn start"
                  @click="handleStartAttendance"
                >
                  <span class="btn-indicator"></span>
                  <span class="btn-text">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                    Iniciar Atendimento
                  </span>
                </button>
                <template v-else>
                  <button class="pms-btn end" @click="handleEndAttendance">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                      <rect width="14" height="14" x="5" y="5" rx="2" ry="2" />
                    </svg>
                    Finalizar Atendimento
                  </button>
                  <button class="pms-btn pause">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <rect width="4" height="14" x="6" y="5" rx="1" />
                      <rect width="4" height="14" x="14" y="5" rx="1" />
                    </svg>
                    Pausar Atendimento
                  </button>
                </template>
                <!-- NOA button will be injected here by AIAssistantAttendanceStartCTA -->
              </div>
            </li>
          </ul>

          <div class="card-footer">
            <span class="footer-note">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" x2="12" y1="8" y2="12" />
                <line x1="12" x2="12.01" y1="16" y2="16" />
              </svg>
              NOA AI Assistant CTA appears below start button
            </span>
          </div>
        </div>

      </main>
    </div>

    <!-- AI Assistant Component -->
    <AIAssistant v-bind="assistantProps" />

    <!-- Dev Panel (floating) -->
    <DevPanel />
  </template>

  <!-- Non-Localhost: Unauthorized Access -->
  <template v-else>
    <div class="unauthorized-container">
      <div class="unauthorized-card">
        <div class="unauthorized-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="48" height="48">
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h1 class="unauthorized-title">Unauthorized Access</h1>
        <p class="unauthorized-message">
          This environment is restricted to local development only.
        </p>
        <div class="unauthorized-hint">
          <span class="hint-label">Allowed origin:</span>
          <code>localhost</code>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import AIAssistant from "./components/ai-assistant/AIAssistant.vue";
import DevPanel from "./components/dev/DevPanel.vue";
import { useKeyboardShortcuts } from "./composables/useKeyboardShortcuts";
import { useTestConfig } from "./composables/useTestConfig";
import { isLocalEnv } from "@/utils/IdentifyGlobalEnv";

// Initialize keyboard shortcuts for dev panel
useKeyboardShortcuts();

// Check if we're in local environment
const isLocalEnvironment = computed(() => isLocalEnv());

// Use shared test config
const { config: testConfig } = useTestConfig();

// Computed assistant props that combine shared config
const assistantProps = computed(() => ({
  position: testConfig.value.position,
  token: testConfig.value.token,
  userId: testConfig.value.userId,
  patientId: testConfig.value.patientId,
  licenseId: testConfig.value.licenseId,
  bookingId: testConfig.value.bookingId,
  pms: testConfig.value.pms,
  professionalId: testConfig.value.professionalId,
  config: {
    autoStart: testConfig.value.autoStart,
    featureFlags: [] as string[],
  },
}));

const attendanceStarted = ref(false);

function handleStartAttendance() {
  attendanceStarted.value = true;
}

function handleEndAttendance() {
  attendanceStarted.value = false;
}

// Dev-only: mirror testConfig into document.userProperties for easier dev integration
function setDevUserProperties() {
  const config = testConfig.value;
  (document as any).userProperties = {
    ...(document as any).userProperties,
    usuarioID: config.userId,
    pacienteID: config.patientId,
    licencaID: config.licenseId,
    agendamentoID: config.bookingId,
    profissionalID: config.professionalId,
  };
}

// Dev-only: Mock iPront function for local development
if (typeof (window as any).iPront === "undefined") {
  (window as any).iPront = (
    type: string,
    patientId: number,
    formId: number,
    insertedFormId: string,
    extra: string
  ) => {
    console.log("🎉 [DEV] iPront called with:", { type, patientId, formId, insertedFormId, extra });
  };
}

// Keep document.userProperties in sync with test config
watch(testConfig, () => setDevUserProperties(), { deep: true, immediate: true });
</script>

<style lang="scss" scoped>
// CSS Variables - scoped to dev container only
$color-bg-primary: #0f0f1a;
$color-bg-secondary: #1a1a2e;
$color-bg-card: rgba(26, 26, 46, 0.85);
$color-accent: #6366f1;
$color-accent-hover: #818cf8;
$color-success: #10b981;
$color-warning: #f59e0b;
$color-text-primary: #f1f5f9;
$color-text-secondary: #94a3b8;
$color-text-muted: #64748b;
$color-border: rgba(255, 255, 255, 0.08);
$radius-lg: 16px;
$radius-md: 12px;
$radius-sm: 8px;

// Background
.app-background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, $color-bg-primary 0%, #1e1b4b 50%, $color-bg-secondary 100%);
  z-index: -2;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at 50% 0%, rgba(99, 102, 241, 0.15) 0%, transparent 60%);
    z-index: -1;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%236366f1' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
  }
}

// Container
.app-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 40px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: $color-text-primary;
}

// Hero Section
.hero {
  text-align: center;
  padding: 48px 32px;
  background: $color-bg-card;
  border: 1px solid $color-border;
  border-radius: $radius-lg;
  backdrop-filter: blur(20px);
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.hero-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.logo {
  height: 42px;
  filter: brightness(0) invert(1);
  opacity: 0.9;
}

.logo-badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: $color-accent;
  background: rgba(99, 102, 241, 0.15);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid rgba(99, 102, 241, 0.3);
}

.hero-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, $color-text-primary 0%, $color-text-secondary 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1rem;
  color: $color-text-secondary;
  margin: 0;
}

.hero-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  padding: 10px 16px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: $radius-sm;
  border: 1px solid $color-border;

  kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    padding: 4px 8px;
    font-size: 11px;
    font-family: "SF Mono", "Consolas", monospace;
    font-weight: 600;
    color: $color-text-primary;
    background: linear-gradient(180deg, #2a2a3a 0%, #1e1e2e 100%);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  span {
    color: $color-text-muted;
    font-size: 12px;
  }

  .hint-text {
    margin-left: 8px;
    color: $color-text-secondary;
  }
}

// Main Content
.main-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// Simulation Card
.simulation-card {
  background: $color-bg-card;
  border: 1px solid $color-border;
  border-radius: $radius-lg;
  backdrop-filter: blur(20px);
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $color-border;
  background: rgba(255, 255, 255, 0.02);
}

.card-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(99, 102, 241, 0.05) 100%);
  border-radius: $radius-md;
  color: $color-accent;
}

.card-title-group {
  flex: 1;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: $color-text-primary;
}

.card-subtitle {
  font-size: 0.8rem;
  margin: 0;
  color: $color-text-muted;
}

// Sidebar Simulation (Feegow PMS structure)
.sidebar-simulation {
  list-style: none;
  margin: 0;
  padding: 20px 24px;

  li {
    list-style: none;
  }
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid $color-border;
  border-radius: $radius-md;
  max-width: 280px;
  margin: 0 auto;
}

// PMS Buttons
.pms-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid $color-border;
  background: rgba(255, 255, 255, 0.03);
  color: $color-text-secondary;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: $color-text-primary;
  }

  &.start {
    position: relative;
    padding-left: 0;
    overflow: hidden;

    .btn-indicator {
      width: 4px;
      height: 100%;
      background: $color-success;
      border-radius: $radius-sm 0 0 $radius-sm;
    }

    .btn-text {
      display: flex;
      align-items: center;
      gap: 10px;
      padding-left: 12px;
      color: $color-success;
    }

    &:hover {
      background: rgba(16, 185, 129, 0.1);
      border-color: rgba(16, 185, 129, 0.3);
    }
  }

  &.end {
    background: rgba(99, 102, 241, 0.1);
    border-color: rgba(99, 102, 241, 0.3);
    color: $color-accent;

    &:hover {
      background: rgba(99, 102, 241, 0.15);
    }
  }

  &.pause {
    background: rgba(245, 158, 11, 0.1);
    border-color: rgba(245, 158, 11, 0.3);
    color: $color-warning;

    &:hover {
      background: rgba(245, 158, 11, 0.15);
    }
  }
}

// Card Footer
.card-footer {
  padding: 12px 24px;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid $color-border;
}

.footer-note {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: $color-text-muted;

  svg {
    opacity: 0.6;
  }
}

// Unauthorized View
.unauthorized-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $color-bg-primary 0%, #1e1b4b 50%, $color-bg-secondary 100%);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.unauthorized-card {
  text-align: center;
  padding: 48px 56px;
  background: $color-bg-card;
  border: 1px solid $color-border;
  border-radius: $radius-lg;
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  max-width: 400px;
}

.unauthorized-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.05) 100%);
  border-radius: 50%;
  color: #ef4444;
}

.unauthorized-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 12px 0;
  color: $color-text-primary;
}

.unauthorized-message {
  font-size: 0.95rem;
  color: $color-text-secondary;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.unauthorized-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: $radius-sm;
  border: 1px solid $color-border;

  .hint-label {
    font-size: 12px;
    color: $color-text-muted;
  }

  code {
    font-size: 12px;
    font-family: "SF Mono", "Consolas", monospace;
    color: $color-accent;
    background: rgba(99, 102, 241, 0.1);
    padding: 2px 8px;
    border-radius: 4px;
  }
}

</style>
